import { FC, memo, useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { PaginationState, Row } from "@tanstack/react-table"
import { BBColumnDef, Integration, IntegrationType } from "../../../../types"
import Table from "../../table"
import PreviewModal from "../../preview-modal"
import ApprovedUrlsModal from "../../approved-urls-modal"
import { LightBtn } from "../../buttons"
import { useAppDispatch, useInMobileView } from "../../../hooks"
import { setPreviewModal } from "../../../../redux/slices/integrations"
import { IntegrationCard, IntegrationCell } from "../table/cells"
import IntegrationCardSkeleton from "../table/components/IntegrationCardSkeleton"
import { ListSkeleton } from "../../skeletons"
import If from "../../if"
import { DateSectionHeader, groupByDate } from "../date-grouping"

interface TabDef {
  label: string
  type: IntegrationType | "all"
}

const TABS: TabDef[] = [
  { label: "All", type: "all" },
  { label: "Standalone", type: "standalone" },
  { label: "API", type: "api" },
  { label: "Loan Officer Page", type: "boomtown" },
  { label: "Website Chat", type: "chatbot" }
]

const VALID_TABS: Array<IntegrationType | "all"> = [
  "all",
  "standalone",
  "api",
  "boomtown",
  "chatbot"
]

const TAB_URL_PARAM = "integration_type"
const PAGE_SIZE = 10

interface Props {
  todayIntegrations?: Integration[]
  /** Full (unsliced) history list — pagination happens client-side */
  integrations?: Integration[]
  isLoading: boolean
  onSelect?: (id: string) => void
  /** Show inner type tabs (All / Standalone / API / Loan Officer Page / Website Chat) */
  showTypeTabs?: boolean
  /** Show "Approved URL's" admin action */
  isAdmin?: boolean
  /** Org id to use for the Approved URL's modal (defaults to the current user's org). */
  approvedUrlsOrgId?: string
}

function matchesTab(
  integration: Integration,
  type: IntegrationType | "all"
): boolean {
  if (type === "all") return true
  return integration.type === type
}

const IntegrationTable: FC<Props> = memo(
  ({
    todayIntegrations = [],
    integrations = [],
    isLoading = false,
    onSelect,
    showTypeTabs = false,
    isAdmin = false,
    approvedUrlsOrgId
  }) => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const isMobile = useInMobileView()
    const listViewType = isMobile ? "grid" : "table"
    const [searchParams, setSearchParams] = useSearchParams()

    const tabFromUrl = showTypeTabs ? searchParams.get(TAB_URL_PARAM) : null
    const initialTab = (
      VALID_TABS.includes(tabFromUrl as any) ? tabFromUrl : "all"
    ) as IntegrationType | "all"

    const pageFromUrl = searchParams.get("page")
    const initialPage = pageFromUrl ? parseInt(pageFromUrl, 10) : 0

    const [activeTab, setActiveTab] = useState<IntegrationType | "all">(
      initialTab
    )
    const [pagination, setPagination] = useState<PaginationState>({
      pageIndex: initialPage,
      pageSize: PAGE_SIZE
    })
    const [approvedUrlsModalOpen, setApprovedUrlsModalOpen] = useState(false)

    const filteredToday = useMemo(
      () =>
        showTypeTabs
          ? todayIntegrations.filter(i => matchesTab(i, activeTab))
          : todayIntegrations,
      [todayIntegrations, activeTab, showTypeTabs]
    )

    const filteredHistory = useMemo(
      () =>
        showTypeTabs
          ? integrations.filter(i => matchesTab(i, activeTab))
          : integrations,
      [integrations, activeTab, showTypeTabs]
    )

    const totalCount = filteredHistory.length

    const pageCount = useMemo(
      () => Math.ceil(totalCount / pagination.pageSize) || 1,
      [totalCount, pagination.pageSize]
    )

    const pagedHistory = useMemo(() => {
      const start = pagination.pageIndex * pagination.pageSize
      return filteredHistory.slice(start, start + pagination.pageSize)
    }, [filteredHistory, pagination.pageIndex, pagination.pageSize])

    const historyGroups = useMemo(
      () => groupByDate(pagedHistory),
      [pagedHistory]
    )

    const hasNoResults =
      filteredToday.length === 0 && filteredHistory.length === 0

    const handleTabChange = (type: IntegrationType | "all") => {
      setActiveTab(type)
      setPagination(p => ({ ...p, pageIndex: 0 }))
      setSearchParams(prev => {
        const next = new URLSearchParams(prev)
        if (type === "all") next.delete(TAB_URL_PARAM)
        else next.set(TAB_URL_PARAM, type)
        next.delete("page")
        return next
      })
    }

    useEffect(() => {
      const currentPage = searchParams.get("page")
      const expectedPage =
        pagination.pageIndex === 0 ? null : String(pagination.pageIndex)

      const currentTab = showTypeTabs ? searchParams.get(TAB_URL_PARAM) : null
      const expectedTab = showTypeTabs && activeTab !== "all" ? activeTab : null

      if (currentPage !== expectedPage || currentTab !== expectedTab) {
        setSearchParams(
          prev => {
            const next = new URLSearchParams(prev)
            if (pagination.pageIndex === 0) next.delete("page")
            else next.set("page", String(pagination.pageIndex))

            if (showTypeTabs) {
              if (activeTab === "all") next.delete(TAB_URL_PARAM)
              else next.set(TAB_URL_PARAM, activeTab)
            }
            return next
          },
          { replace: true }
        )
      }
    }, [
      pagination.pageIndex,
      activeTab,
      showTypeTabs,
      searchParams,
      setSearchParams
    ])

    const handleSelectIntegration = useCallback(
      (id: string) => {
        if (onSelect) {
          onSelect(id)
        } else {
          navigate(`/integrations/${id}`)
        }
      },
      [onSelect, navigate]
    )

    const renderCard = useCallback(
      (row: Row<Integration>) => (
        <IntegrationCard
          data={row.original}
          onSelect={handleSelectIntegration}
        />
      ),
      [handleSelectIntegration]
    )

    const columns: BBColumnDef<Integration>[] = useMemo(
      () => [
        {
          id: "title",
          header: () => <>Name</>,
          footer: props => props.column.id,
          cell: info => (
            <IntegrationCell
              data={info.row.original}
              onSelect={handleSelectIntegration}
            />
          )
        }
      ],
      [handleSelectIntegration]
    )

    const getTabButtonClassName = (isActive: boolean): string =>
      [
        "rounded-full px-4 py-1.5 text-sm border transition-colors",
        isActive
          ? "bg-[#f4effe] border-[#4a1a9d] text-[#4a1a9d] font-medium"
          : "bg-white border-[#e4e4e4] text-base-dark font-normal"
      ].join(" ")

    const showToolbar = showTypeTabs || isAdmin

    return (
      <div className="mt-6">
        <If
          condition={isLoading}
          then={
            isMobile ? (
              <div className="flex flex-col gap-3 py-2 mt-4">
                {[...Array(4)].map((_, i) => (
                  <IntegrationCardSkeleton key={i} />
                ))}
              </div>
            ) : (
              <ListSkeleton />
            )
          }
          else={
            <div className="px-4">
              {showToolbar && (
                <div className="flex items-center justify-between gap-4 flex-wrap pt-2">
                  {showTypeTabs ? (
                    <div className="flex items-center gap-2 overflow-x-auto snap-x snap-mandatory scrollbar-none pb-0.5">
                      {TABS.map(tab => (
                        <button
                          key={tab.type}
                          type="button"
                          onClick={() => handleTabChange(tab.type)}
                          className={`snap-start shrink-0 ${getTabButtonClassName(
                            activeTab === tab.type
                          )}`}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div />
                  )}
                  {isAdmin && (
                    <LightBtn
                      className="hidden sm:flex"
                      onClick={() => setApprovedUrlsModalOpen(true)}
                    >
                      Approved URL&apos;S
                    </LightBtn>
                  )}
                </div>
              )}

              <div
                className={
                  isMobile ? "mt-4 -mx-6 px-6 py-4 bg-primary-100" : "mt-4"
                }
              >
                {hasNoResults && (
                  <div className="flex min-h-[240px] items-center justify-center">
                    <p className="text-sm text-[#757575]">No results found.</p>
                  </div>
                )}

                {filteredToday.length > 0 && (
                  <div>
                    <DateSectionHeader label="TODAY" />
                    <Table
                      data={filteredToday}
                      columns={columns}
                      viewType={listViewType}
                      pageSize={Math.max(filteredToday.length, 1)}
                      pageCount={1}
                      searchBarVisible={false}
                      noHeader
                      removeMobilePagination
                      renderCard={renderCard}
                      gridClass="flex flex-col gap-3 py-2"
                      cardClassName="text-left bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                    />
                  </div>
                )}

                {historyGroups.map(group => (
                  <div key={group.label}>
                    <DateSectionHeader label={group.label} />
                    <Table
                      data={group.items}
                      columns={columns}
                      viewType={listViewType}
                      pageSize={Math.max(group.items.length, 1)}
                      pageCount={1}
                      searchBarVisible={false}
                      noHeader
                      removeMobilePagination
                      renderCard={renderCard}
                      gridClass="flex flex-col gap-3 py-2"
                      cardClassName="text-left bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                    />
                  </div>
                ))}

                {pageCount > 1 && (
                  <Table
                    className="mt-4"
                    data={[]}
                    columns={columns}
                    viewType={listViewType}
                    pageSize={pagination.pageSize}
                    pageCount={pageCount}
                    pagination={pagination}
                    setPagination={setPagination}
                    rowCount={totalCount}
                    searchBarVisible={false}
                    noHeader
                    removeMobilePagination
                    noDataElement={<></>}
                  />
                )}
              </div>
              <PreviewModal
                onClose={() => {
                  dispatch(
                    setPreviewModal({
                      isOpen: false,
                      appKey: ""
                    })
                  )
                }}
              />
              {isAdmin && (
                <ApprovedUrlsModal
                  show={approvedUrlsModalOpen}
                  toggleModal={setApprovedUrlsModalOpen}
                  orgId={approvedUrlsOrgId}
                />
              )}
            </div>
          }
        />
      </div>
    )
  }
)

IntegrationTable.displayName = "IntegrationTable"

export default IntegrationTable
